/**
 * Navigation model shared by the desktop SideNav and the mobile top bar menu,
 * so both surfaces always offer the same destinations.
 */
import type { ComponentType } from 'react';
import {
  BellIcon,
  BuddiesIcon,
  CreateIcon,
  HomeIcon,
  MessagesIcon,
  UserIcon,
  RobiIcon,
  SettingsIcon,
  BookmarkIcon,
  LockIcon,
  ShieldIcon,
  BlockIcon,
  BlogIcon,
  ChartIcon,
  AwardIcon,
  LeaderboardIcon,
  HeadsetIcon,
  InfoIcon,
  PhoneIcon,
  type IconProps,
} from '../ui/icons';

export interface NavItem {
  to: string;
  labelKey: string;
  icon: ComponentType<IconProps>;
  requiresAuth: boolean;
  end?: boolean;
  /** Reachable from the mobile menu instead of the bottom tab bar. */
  hideOnMobileTabBar?: boolean;
  /** Bottom tab bar position; the sidebar keeps the declaration order. */
  mobileTabOrder?: number;
  /** Rendered as the raised centre action of the bottom tab bar. */
  primaryAction?: boolean;
}

export interface MoreItem {
  labelKey: string;
  icon: ComponentType<IconProps>;
  to: string;
  /** Defaults to true — only public destinations opt out. */
  requiresAuth?: boolean;
}

export interface MoreSubmenu {
  key: string;
  labelKey: string;
  icon: ComponentType<IconProps>;
  items: MoreItem[];
}

export const NAV_ITEMS: NavItem[] = [
  {
    to: '/',
    labelKey: 'home.tabs.home',
    icon: HomeIcon,
    requiresAuth: false,
    end: true,
    mobileTabOrder: 1,
  },
  {
    to: '/buddies',
    labelKey: 'home.tabs.buddies',
    icon: BuddiesIcon,
    requiresAuth: true,
    mobileTabOrder: 2,
  },
  {
    to: '/notifications',
    labelKey: 'home.tabs.notifications',
    icon: BellIcon,
    requiresAuth: true,
    mobileTabOrder: 4,
  },
  {
    to: '/messages',
    labelKey: 'home.tabs.messages',
    icon: MessagesIcon,
    requiresAuth: true,
    hideOnMobileTabBar: true,
  },
  {
    to: '/robi',
    labelKey: 'home.tabs.robi',
    icon: RobiIcon,
    requiresAuth: true,
    mobileTabOrder: 5,
  },
  {
    to: '/create',
    labelKey: 'home.rightBanner.createPost',
    icon: CreateIcon,
    requiresAuth: true,
    mobileTabOrder: 3,
    primaryAction: true,
  },
];

/** Grouped so a divider can separate account, content and progress entries. */
export const MORE_SECTIONS: MoreItem[][] = [
  [
    { labelKey: 'sidebar.menu.blockedUsers', icon: BlockIcon, to: '/blocked-users' },
    { labelKey: 'sidebar.menu.changePassword', icon: LockIcon, to: '/change-password' },
  ],
  [
    { labelKey: 'sidebar.menu.blog', icon: BlogIcon, to: '/blog' },
    { labelKey: 'sidebar.menu.trackProgress', icon: ChartIcon, to: '/tracker' },
  ],
  [
    { labelKey: 'sidebar.menu.chooseAiBuddy', icon: BuddiesIcon, to: '/choose-ai-buddy' },
    { labelKey: 'sidebar.menu.savedPosts', icon: BookmarkIcon, to: '/saved-posts' },
    { labelKey: 'sidebar.menu.awards', icon: AwardIcon, to: '/awards' },
    { labelKey: 'sidebar.menu.leaderBoard', icon: LeaderboardIcon, to: '/leaderboard' },
  ],
];

export const MORE_SUBMENUS: MoreSubmenu[] = [
  {
    key: 'settings',
    labelKey: 'sidebar.sections.settings',
    icon: SettingsIcon,
    items: [
      { labelKey: 'sidebar.settings.aiSettings', icon: RobiIcon, to: '/settings/ai' },
      { labelKey: 'sidebar.settings.accountSettings', icon: UserIcon, to: '/settings/account' },
    ],
  },
  {
    key: 'support',
    labelKey: 'sidebar.sections.supportLegal',
    icon: HeadsetIcon,
    items: [
      { labelKey: 'sidebar.support.help', icon: InfoIcon, to: '/support/guide' },
      { labelKey: 'sidebar.support.contactUs', icon: PhoneIcon, to: '/support/contact' },
      {
        labelKey: 'sidebar.support.termsOfUse',
        icon: BlogIcon,
        to: '/support/terms',
        requiresAuth: false,
      },
      {
        labelKey: 'sidebar.support.privacyPolicy',
        icon: ShieldIcon,
        to: '/support/privacy',
        requiresAuth: false,
      },
    ],
  },
];

export const MORE_ROUTES: MoreItem[] = [
  ...MORE_SECTIONS.flat(),
  ...MORE_SUBMENUS.flatMap(menu => menu.items),
];
